import { json, LoaderFunctionArgs, MetaDescriptor } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { formatISO } from 'date-fns';

import { getDestinationMetadata } from '@/constants/destinationsMetadata';
import { useCurrencyRevalidator } from '@/hooks/useCurrencyRevalidator';
import { useDeviceOS } from '@/hooks/useDeviceOS';
import PlanSelection from '@/pages/PlanSelection';
import {
  getGlobalAppRatingsContent,
  getHelpCentreContent,
} from '@/services/cms/CmsServices';
import { getPlanSelectionCountries } from '@/services/PlanSelectionService/PlanSelectionsService';
import { getProductOfferings } from '@/services/product/ProductOfferingService';
import {
  getAllCountries,
  getFeaturedCountries,
} from '@/services/resources/ResourceService';
import { getCountryUtils } from '@/utils/countryUtils';
import { digestAppRatings } from '@/utils/digestAppRatings';
import { getMediaUrlForSize } from '@/utils/mediaUtils';
import { getStaticPageTitle, mergeMeta } from '@/utils/metaUtils';
import { isMobileApp } from '@/utils/platformUtils';
import { getConfig, getCurrency, getServerUserAgent } from '@/utils/ssrUtils';
import {
  getLayoutPlans,
  getLayoutTestimonials,
} from '~/helpers/pageLayoutData';
import i18nextRemix from '~/i18next.server';

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const { countrySlug } = params;
  if (!countrySlug) {
    throw new Response('Not Found', { status: 404 });
  }

  const t = await i18nextRemix.getFixedT(request);
  const { getCountryNameTranslation } = getCountryUtils(t);

  const config = await getConfig();
  const currency = getCurrency({ request, config });

  const countries = await getAllCountries();

  const currentCountry = countries.find(
    (country) => countrySlug === country.slug,
  );

  if (!currentCountry) {
    throw new Response(`${countrySlug} Not Found`, { status: 404 });
  }

  const metadata = getDestinationMetadata(t, countrySlug);

  const [products, cmsContent, featuredCountries, cmsAppRatings] =
    await Promise.all([
      getProductOfferings({
        country_id: currentCountry.id,
        travel_date: formatISO(new Date(), { representation: 'date' }),
        currency: getCurrency({ request, config }),
        locale: params.lang,
      }),
      getPlanSelectionCountries({
        countryCode: currentCountry.id,
        locale: params.lang,
      }),
      getFeaturedCountries(currency),
      getGlobalAppRatingsContent(),
    ]);

  const title =
    cmsContent?.meta?.title ??
    metadata?.title ??
    t('browser.planSelectionTitle', 'eSIM {{country}}', {
      country: getCountryNameTranslation(currentCountry.description),
    });

  const description =
    cmsContent?.meta?.description ??
    metadata?.description ??
    t(
      'browser.planSelectionDescription',
      'Avoid roaming charges in {{country}} with Sim Local. Great value local & regional Travel eSIMs to keep you connected wherever you are.',
      {
        country: getCountryNameTranslation(currentCountry.description),
      },
    );

  const image = cmsContent?.meta?.image;

  const structuredData = cmsContent?.meta?.structuredData;

  const helpCentre = await getHelpCentreContent({ locale: params.lang });

  const plans = await getLayoutPlans(cmsContent?.layout, currency);
  const testimonials = await getLayoutTestimonials(
    cmsContent?.layout,
    params.lang,
  );

  return json({
    countries,
    currentCountry,
    products,
    helpCentre,
    cmsContent,
    featuredCountries,
    plans,
    testimonials,
    meta: {
      title,
      description,
      image,
      structuredData,
    },
    userAgent: getServerUserAgent(request),
    cmsAppRatings,
  });
};

export const meta = mergeMeta<typeof loader>(({ matches, data }) => {
  if (!data) {
    return [];
  }

  const descriptors: MetaDescriptor[] = [];

  const { title, description, image, structuredData } = data.meta;

  descriptors.push(
    getStaticPageTitle({
      matches,
      title,
    }),
  );

  descriptors.push({
    name: 'description',
    content: description,
  });

  const imageUrl = image ? getMediaUrlForSize(image, 'w1080') : undefined;
  if (imageUrl) {
    descriptors.push({
      property: 'og:image',
      content: imageUrl,
    });
  }

  if (structuredData) {
    structuredData.forEach(({ jsonld }) => {
      descriptors.push({
        'script:ld+json': jsonld,
      });
    });
  }

  return descriptors;
});

export function shouldRevalidate({
  nextUrl,
  currentUrl,
  defaultShouldRevalidate,
}: {
  nextUrl: URL;
  currentUrl: URL;
  defaultShouldRevalidate: boolean;
}) {
  /*
  Since filtering happens in the UI, there is no need of revalidating loader data on filters change.
  We need revalidation though, when currency changes.
  That's why we have this optimisation, to avoid revalidation on query params change, but allow it, when url doesn't change,
  as it indicates currency change.
   */
  if (
    nextUrl.searchParams.toString() !== currentUrl.searchParams.toString() &&
    nextUrl.pathname === currentUrl.pathname
  ) {
    return false;
  }
  return defaultShouldRevalidate;
}

export default function Route() {
  const {
    countries,
    currentCountry,
    products,
    helpCentre,
    cmsContent,
    featuredCountries,
    userAgent,
    testimonials,
    plans,
    cmsAppRatings,
  } = useLoaderData<typeof loader>();

  useCurrencyRevalidator();
  const { deviceOS } = useDeviceOS();

  const appRatings = digestAppRatings({
    deviceOS,
    cmsAppRatings,
    isMobileApp,
  });

  return (
    <PlanSelection
      plans={plans}
      testimonials={testimonials}
      helpCentre={helpCentre}
      featuredCountries={featuredCountries}
      countries={countries}
      currentCountry={currentCountry}
      products={products}
      cmsContent={cmsContent}
      userAgent={userAgent}
      appRatings={appRatings}
    />
  );
}
